<script lang="ts">
  interface Insight {
    id: number
    kind: string
    title: string
    body: string
    source_entity: { id: number; kind: string; name: string } | null
    target_entity: { id: number; kind: string; name: string } | null
    source_entity_id: number | null
    target_entity_id: number | null
    video_id: string | null
    metadata: Record<string, unknown>
    dismissed_at: string | null
    created_at: string
  }

  interface Props {
    focalEntityId: number | null
    clusterNodeIds?: number[]
  }

  let { focalEntityId, clusterNodeIds = [] }: Props = $props()

  let insights = $state<Insight[]>([])
  let loading = $state(false)
  let error = $state<string | null>(null)
  let showDismissed = $state(false)

  const KIND_LABEL: Record<string, string> = {
    initial_ingest: 'Toegevoegd',
    red_thread_shift: 'Rode draad',
    novel_perspective: 'Nieuw perspectief',
    puzzle_piece: 'Puzzelstuk',
    cross_thread_link: 'Cross-thread',
  }

  function entityIdsParam(): string {
    const ids = new Set<number>()
    if (focalEntityId !== null) ids.add(focalEntityId)
    for (const id of clusterNodeIds) ids.add(id)
    return Array.from(ids)
      .map((id) => `entity_ids[]=${id}`)
      .join('&')
  }

  async function load() {
    if (focalEntityId === null) {
      insights = []
      return
    }
    loading = true
    error = null
    try {
      const dismissedFilter = showDismissed ? 'true' : 'false'
      const url = `/api/insights?dismissed=${dismissedFilter}&${entityIdsParam()}`
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) throw new Error(`insights request failed: ${response.status}`)
      const data = await response.json()
      insights = data.insights as Insight[]
    } catch (err) {
      error = err instanceof Error ? err.message : String(err)
    } finally {
      loading = false
    }
  }

  async function dismissInsight(insightId: number) {
    try {
      const response = await fetch(`/api/insights/${insightId}/dismiss`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) throw new Error(`update failed: ${response.status}`)
      insights = insights.filter((insight) => insight.id !== insightId)
    } catch (err) {
      error = err instanceof Error ? err.message : String(err)
    }
  }

  function isFallback(insight: Insight): boolean {
    return insight.metadata?.['body_source'] === 'fallback'
  }

  function hasPairedTarget(insight: Insight): boolean {
    return insight.target_entity_id !== null
  }

  const clusterKey = $derived([...clusterNodeIds].sort((a, b) => a - b).join(','))

  $effect(() => {
    void focalEntityId
    void clusterKey
    void showDismissed
    load()
  })
</script>

<div class="insights-sidebar" data-testid="insights-sidebar">
  <header class="insights-header">
    <h2 class="insights-title">Insights</h2>
    <button
      type="button"
      class="insights-toggle"
      onclick={() => (showDismissed = !showDismissed)}
      data-testid="toggle-dismissed"
    >
      {showDismissed ? 'Toon actieve' : 'Toon verborgen'}
    </button>
  </header>
  {#if loading}
    <p class="insights-status">Laden...</p>
  {:else if error}
    <p class="insights-status insights-error">Kon insights niet laden: {error}</p>
  {:else if insights.length === 0}
    <p class="insights-status">
      {showDismissed
        ? 'Geen verborgen insights voor deze cluster.'
        : 'Nog geen insights voor deze cluster.'}
    </p>
  {:else}
    <ul class="insights-list">
      {#each insights as insight (insight.id)}
        <li class="insight-card" data-testid={`insight-${insight.id}`}>
          <div class="insight-meta">
            <span class="insight-kind">{KIND_LABEL[insight.kind] ?? insight.kind}</span>
            <div class="insight-meta-right">
              {#if isFallback(insight)}
                <span class="insight-fallback" title="Automatische tekst (LLM gaf geen output)">
                  fallback
                </span>
              {/if}
              {#if !showDismissed}
                <button
                  class="insight-dismiss"
                  type="button"
                  onclick={() => dismissInsight(insight.id)}
                  aria-label="Insight verbergen"
                >
                  verbergen
                </button>
              {/if}
            </div>
          </div>
          <h3 class="insight-title">{insight.title}</h3>
          <p class="insight-body">{insight.body}</p>
          {#if insight.source_entity_id || hasPairedTarget(insight)}
            <p class="insight-entities">
              <span class={`insight-entity${insight.source_entity ? '' : ' insight-entity-missing'}`}>
                {insight.source_entity ? insight.source_entity.name : '[verwijderd]'}
              </span>
              {#if hasPairedTarget(insight)}
                <span class="insight-entity-divider">→</span>
                <span class={`insight-entity${insight.target_entity ? '' : ' insight-entity-missing'}`}>
                  {insight.target_entity ? insight.target_entity.name : '[verwijderd]'}
                </span>
              {/if}
            </p>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .insights-sidebar {
    height: 100%;
    overflow-y: auto;
    padding: 1rem 1.25rem;
    background: #f8fafc;
    border-left: 1px solid #e2e8f0;
  }
  .insights-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }
  .insights-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    color: #0f172a;
  }
  .insights-toggle {
    background: none;
    border: 1px solid #cbd5e1;
    border-radius: 9999px;
    padding: 0.2rem 0.7rem;
    font-size: 0.75rem;
    color: #475569;
    cursor: pointer;
  }
  .insights-toggle:hover {
    border-color: #94a3b8;
    color: #0f172a;
  }
  .insights-status {
    color: #64748b;
    font-style: italic;
  }
  .insights-error {
    color: #b91c1c;
  }
  .insights-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .insight-card {
    padding: 0.75rem 1rem;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  }
  .insight-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
    color: #64748b;
  }
  .insight-meta-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .insight-kind {
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .insight-fallback {
    background: #fef3c7;
    color: #92400e;
    padding: 0.05rem 0.45rem;
    border-radius: 9999px;
    font-size: 0.7rem;
  }
  .insight-dismiss {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    font-size: 0.75rem;
  }
  .insight-dismiss:hover {
    color: #b91c1c;
  }
  .insight-title {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0.4rem 0 0.3rem;
    color: #0f172a;
  }
  .insight-body {
    font-size: 0.85rem;
    line-height: 1.4;
    color: #334155;
    margin: 0 0 0.5rem;
  }
  .insight-entities {
    margin: 0;
    font-size: 0.75rem;
    color: #475569;
  }
  .insight-entity {
    background: #e0e7ff;
    color: #312e81;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
  }
  .insight-entity-missing {
    background: #fee2e2;
    color: #991b1b;
    font-style: italic;
  }
  .insight-entity-divider {
    margin: 0 0.4rem;
    color: #94a3b8;
  }
</style>
